import { AssociateAuditTrailService } from '@services/AssociateAuditTrailService';
import { AssociateDurationService, IBaseDurationEvent } from '@services/AssociateDurationService';
import { CrossFrameUtils } from '@utils/CrossFrameUtils';
import { AppInfoLoader, IAppInfoType } from '@services/AppInfoLoader';
import { EnvironmentDetail, IZoneType } from '@utils/EnvironmentDetailUtils';
import { ATConfigService } from '@services/ATConfigService';
import { WeblabService } from '@services//WeblabService';

interface IToolDurationEvent extends IBaseDurationEvent {
    Tool: string;
    Zone: string;
    Subzone: string;
    Seconds: number;
    ToolParams?: string[];
}

interface IToolStartDurationEvent extends Omit<IToolDurationEvent, 'Seconds'> {
    SessionId: string;
}

interface IToolAndZoneDurationEvent extends IToolDurationEvent {
    SessionId: string;
    ToolZone?: string;
    ToolSubZone?: string;
    ToolPriorityBucket?: number;
}

interface IToolZoneType {
    toolZone: string;
    toolSubzone: string;
    toolPriorityBucket: number;
}

interface IHeartbeat {
    count: number;
    storeId: string;
    toolName: string;
    startTimestamp: number;
    endTimestamp: number;
    zoneInfo: IZoneType;
    toolZoneInfo: IToolZoneType;
    masterIndirectTask?: string;
    toolParams: string[];
}

const TOOL_HEARTBEAT_STORAGE_KEY = 'toolHeartbeat';
const HEARTBEAT_INTERVAL_IN_MILLISECONDS = 1000;
const SAVING_INTERVAL_IN_SECONDS = 60;
const DEFAULT_TOOL_TIMEOUT_IN_SECONDS = 1800;

const TOOL_DURATION_PROPERTIES = {
    PROCESS_NAME: 'ToolDuration',
    PROCESS_NAME_V2: 'ToolDurationV2',
    EVENT_TYPE: 'ToolDuration',
};

const TOOL_START_PROPERTIES = {
    PROCESS_NAME: 'ToolStart',
};

export class ToolHeartbeatService {
    private static currentToolZoneInfo: IToolZoneType;
    private static lastActivityTimestamp: number;
    private static toolTimeoutInSeconds: number = DEFAULT_TOOL_TIMEOUT_IN_SECONDS;
    private static startTimestamp: number;
    private static currentStoreId: string;
    private static currentZoneInfo: IZoneType;
    private static currentToolName: string;
    private static heartbeatCount: number;
    private static interval: NodeJS.Timeout;
    private static currentToolParams: string[];

    public static async init() {
        try {
            const appConfig: IAppInfoType = await AppInfoLoader.getAppConfig();
            const store = appConfig.store;
            const toolName = appConfig.toolName;
            const zoneInfo = appConfig.zoneInfo;
            ToolHeartbeatService.initListeners(store, toolName, zoneInfo);
            ToolHeartbeatService.startAudit(store, toolName, zoneInfo);
            ToolHeartbeatService.setActivityOccurred();
            document.addEventListener(
                'touchend',
                function () {
                    ToolHeartbeatService.setActivityOccurred();
                },
                true,
            );
            document.addEventListener(
                'focusin',
                function () {
                    ToolHeartbeatService.setActivityOccurred();
                },
                true,
            );
            const toolConfigs = await ATConfigService.getToolConfigsByName(store, toolName);
            if (toolConfigs) {
                ToolHeartbeatService.setToolTimeout(Number(toolConfigs.timeout) * 60);
            }
        } catch (error) {
            console.error('Failed to getAppConfig', error);
        }
    }

    public static initListeners(store: string, toolName: string, zoneInfo: IZoneType) {
        CrossFrameUtils.registerListener('stopAndFlushToolHeartbeat', () => {
            ToolHeartbeatService.flush();
            return Promise.resolve({});
        });

        CrossFrameUtils.registerListener('restartToolHeartbeat', function () {
            ToolHeartbeatService.startAudit(
                ToolHeartbeatService.currentStoreId || store,
                ToolHeartbeatService.currentToolName || toolName,
                ToolHeartbeatService.currentZoneInfo || zoneInfo,
            );
            return Promise.resolve({});
        });
    }

    /**
     * Sends the latest heartbeat that was saved (if one exists). Then starts
     * creating heartbeats for the current tool
     *
     * @param storeId String - the storeId
     * @param toolName String - the name of the tool that we are keeping track
     * @param zoneInfo IZoneType - the name of the zone/subzone that we are keeping track
     * @param toolZoneInfo IToolZoneType - Optional parrameter containing tool specific zone info
     * of
     * @param toolParams String[] - Optional parameter containing ProblemSolve workflow configurations
     */
    public static startAudit(storeId: string, toolName: string, zoneInfo: IZoneType, toolZoneInfo?: IToolZoneType, toolParams?: string[]) {
        console.log('ATBase ToolHeartBeatService emitToolStartEvent startAudit called');
        ToolHeartbeatService.currentToolZoneInfo = null;
        ToolHeartbeatService.currentToolParams = null;
        ToolHeartbeatService.flush();
        ToolHeartbeatService.resetHeartbeat(storeId, toolName, zoneInfo, toolZoneInfo, toolParams);
        ToolHeartbeatService.emitToolStartEvent();
        ToolHeartbeatService.updateHeartbeatOnInterval();
    }

    /**
     * Tells toolHeartbeat Service that activity has occurred. It should resume heartbeating if it was paused.
     */
    public static setActivityOccurred() {
        ToolHeartbeatService.lastActivityTimestamp = new Date().getTime();
    }

    /**
     * Tells toolHeartbeat Service that activity has occurred. It should resume heartbeating if it was paused.
     */
    public static setToolTimeout(timeoutInSeconds: number) {
        ToolHeartbeatService.toolTimeoutInSeconds = timeoutInSeconds;
    }

    /**
     * Sends the latest heartbeat that was saved (if one exists)
     */
    public static flush() {
        console.log('ATBase ToolHeartBeatService flush checking heartbeat');
        const heartbeat = ToolHeartbeatService.loadHeartbeat();
        ToolHeartbeatService.restoreModuleToInitialState();

        if (heartbeat) {
            console.log('ATBase ToolHeartBeatService flush found heartbeat: ', JSON.stringify(heartbeat));
            ToolHeartbeatService.emitToolDurationEvent(heartbeat);
            ToolHeartbeatService.emitToolAndZoneDurationEvent(heartbeat);
        }
    }

    /**
     * Returns true if there are recent activities
     */
    public static isActive() {
        return (
            ToolHeartbeatService.lastActivityTimestamp == null ||
            Math.round((new Date().getTime() - ToolHeartbeatService.lastActivityTimestamp) / 1000) <= ToolHeartbeatService.toolTimeoutInSeconds
        );
    }

    /**
     *  Reset heartbeat
     */
    public static resetHeartbeat(storeId: string, toolName: string, zoneInfo: IZoneType, toolZoneInfo?: IToolZoneType, toolParams?: string[]) {
        ToolHeartbeatService.startTimestamp = new Date().getTime();
        ToolHeartbeatService.currentStoreId = storeId;
        ToolHeartbeatService.currentToolName = toolName;
        ToolHeartbeatService.currentZoneInfo = zoneInfo;
        if (toolParams != null) {
            ToolHeartbeatService.currentToolParams = toolParams;
        }
        if (toolZoneInfo != null) {
            ToolHeartbeatService.currentToolZoneInfo = toolZoneInfo;
        }
        ToolHeartbeatService.heartbeatCount = 0;
    }

    /**
     * Updates the heartbeat repeatedly
     */
    private static updateHeartbeatOnInterval() {
        ToolHeartbeatService.updateAndMaybeEmitHeartbeat();
        ToolHeartbeatService.interval = setTimeout(ToolHeartbeatService.updateHeartbeatOnInterval, HEARTBEAT_INTERVAL_IN_MILLISECONDS);
    }

    /**
     * Creates a new heartbeat and saves it
     * Emit a tool duration event if the interval exceed 60 seconds
     */
    private static updateAndMaybeEmitHeartbeat() {
        if (!ToolHeartbeatService.isActive()) {
            return;
        }

        const heartbeat = ToolHeartbeatService.constructHeartbeat();
        ToolHeartbeatService.saveHeartbeat(heartbeat);
        if (ToolHeartbeatService.getToolDuration(heartbeat) >= SAVING_INTERVAL_IN_SECONDS) {
            ToolHeartbeatService.flush();
            ToolHeartbeatService.resetHeartbeat(
                ToolHeartbeatService.currentStoreId,
                ToolHeartbeatService.currentToolName,
                ToolHeartbeatService.currentZoneInfo,
                ToolHeartbeatService.currentToolZoneInfo,
                ToolHeartbeatService.currentToolParams,
            );
        }
    }

    /**
     * Restores the module to the initial state
     */
    private static restoreModuleToInitialState() {
        ToolHeartbeatService.stopInterval();
        ToolHeartbeatService.removeHeartbeat();
    }

    /**
     * Sends a tool duration event to Associate Audit table
     *
     * @param heartbeat Heartbeat - the heartbeat object
     */
    private static emitToolDurationEvent(heartbeat: IHeartbeat) {
        console.log('ATBase ToolHeartBeatService emitToolDurationEvent heartbeat: ', JSON.stringify(heartbeat));

        const baseEvent = AssociateAuditTrailService.generateBaseEvent(TOOL_DURATION_PROPERTIES.PROCESS_NAME);
        const storeId = heartbeat.storeId;
        const seconds = Math.min(ToolHeartbeatService.getToolDuration(heartbeat), SAVING_INTERVAL_IN_SECONDS);
        const associateEvent: IToolDurationEvent = {
            ...baseEvent,
            Tool: heartbeat.toolName,
            Seconds: seconds,
            Zone: heartbeat.zoneInfo?.zoneName,
            Subzone: heartbeat.zoneInfo?.subzoneName,
        };
        AssociateAuditTrailService.recordEventAndEmitMetric(storeId, associateEvent, TOOL_DURATION_PROPERTIES.EVENT_TYPE);
    }

    /**
     * Sends a tool duration event to Associate Duration table
     *
     * @param heartbeat Heartbeat - the heartbeat object
     */
    private static emitToolAndZoneDurationEvent(heartbeat: IHeartbeat) {
        console.log('ATBase ToolHeartBeatService emitToolAndZoneDurationEvent heartbeat: ', JSON.stringify(heartbeat));
        const baseEvent = AssociateDurationService.generateBaseEvent(TOOL_DURATION_PROPERTIES.PROCESS_NAME_V2);
        const storeId = heartbeat.storeId;
        const seconds = Math.min(ToolHeartbeatService.getToolDuration(heartbeat), SAVING_INTERVAL_IN_SECONDS);
        const associateEvent: IToolAndZoneDurationEvent = {
            ...baseEvent,
            SessionId: baseEvent.RequestId,
            Tool: heartbeat.toolName,
            Seconds: seconds,
            Zone: heartbeat.zoneInfo?.zoneName,
            Subzone: heartbeat.zoneInfo?.subzoneName,
        };
        console.log('ATBase ToolHeartBeatService emitToolAndZoneDurationEvent associateEvent: ', JSON.stringify(associateEvent));
        if (heartbeat.toolZoneInfo != null) {
            if (heartbeat.toolZoneInfo.toolZone != null) {
                associateEvent.ToolZone = heartbeat.toolZoneInfo.toolZone;
            }
            if (heartbeat.toolZoneInfo.toolSubzone != null) {
                associateEvent.ToolSubZone = heartbeat.toolZoneInfo.toolSubzone;
            }
            if (heartbeat.toolZoneInfo.toolPriorityBucket != null) {
                associateEvent.ToolPriorityBucket = heartbeat.toolZoneInfo.toolPriorityBucket;
            }
        }
        if (heartbeat.toolParams != null) {
            associateEvent.ToolParams = heartbeat.toolParams;
        }
        AssociateDurationService.recordDurationAndEmitMetric(storeId, associateEvent.Seconds * 1000, associateEvent, TOOL_DURATION_PROPERTIES.PROCESS_NAME_V2);
    }

    /**
     *  Get the time elapsed using the tool
     *
     *  @param heartbeat Heartbeat - the heartbeat object
     */
    private static getToolDuration(heartbeat: IHeartbeat) {
        const startTime = heartbeat.startTimestamp;
        const endTime = heartbeat.endTimestamp;
        return Math.round((endTime - startTime) / 1000);
    }

    /**
     * Creates a new heartbeat with an updated endTimestamp
     *
     * @return heartbeat Heartbeat - the newly created heartbeat
     */
    private static constructHeartbeat(): IHeartbeat {
        const endTimestamp = new Date().getTime();

        const heartbeat: IHeartbeat = {
            count: ++ToolHeartbeatService.heartbeatCount,
            storeId: ToolHeartbeatService.currentStoreId,
            toolName: ToolHeartbeatService.currentToolName,
            startTimestamp: ToolHeartbeatService.startTimestamp,
            endTimestamp,
            zoneInfo: ToolHeartbeatService.currentZoneInfo,
            toolZoneInfo: ToolHeartbeatService.currentToolZoneInfo,
            toolParams: ToolHeartbeatService.currentToolParams,
        };

        return heartbeat;
    }

    /**
     * Gets a heartbeat from storage. If there is no heartbeat stored, undefined
     * is returned
     *
     * @return heartbeat Heartbeat - latest saved heartbeat
     */
    private static loadHeartbeat() {
        const heartbeatJSON = window.localStorage.getItem(TOOL_HEARTBEAT_STORAGE_KEY);

        if (heartbeatJSON) {
            return JSON.parse(heartbeatJSON);
        }

        return undefined;
    }

    /**
     * Removes the saved heartbeat from localStorage
     */
    private static removeHeartbeat() {
        window.localStorage.removeItem(TOOL_HEARTBEAT_STORAGE_KEY);
    }

    /**
     * Saves a heartbeat to localStorage
     *
     * @param heartbeat Heartbeat - heartbeat to persist
     */
    private static saveHeartbeat(heartbeat) {
        const heartbeatJSON = JSON.stringify(heartbeat);
        window.localStorage.setItem(TOOL_HEARTBEAT_STORAGE_KEY, heartbeatJSON);
    }

    /**
     * Stops the interval
     */
    private static stopInterval() {
        if (ToolHeartbeatService.interval) {
            clearTimeout(ToolHeartbeatService.interval);
            ToolHeartbeatService.interval = null;
        }
    }

    private static emitToolStartEvent() {
        console.log('ATBase ToolHeartBeatService emitToolStartEvent');
        const baseEvent = AssociateDurationService.generateBaseEvent(TOOL_START_PROPERTIES.PROCESS_NAME);
        const toolStartEvent: IToolStartDurationEvent = {
            ...baseEvent,
            SessionId: baseEvent.RequestId,
            Tool: ToolHeartbeatService.currentToolName,
            Zone: ToolHeartbeatService.currentZoneInfo?.zoneName,
            Subzone: ToolHeartbeatService.currentZoneInfo?.subzoneName,
        };
        console.log('ATBase emitToolStartEvent toolStartEvent: ', JSON.stringify(toolStartEvent));

        AssociateDurationService.recordDurationAndEmitMetric(ToolHeartbeatService.currentStoreId, 1, toolStartEvent, TOOL_START_PROPERTIES.PROCESS_NAME);
    }
}
